// src/hooks/useRequisitionPool.ts
//
// react-query hooks for the Awaiting Inquiry pool and composing Inquiries
// from it (EE-253 P2b). Composing takes the chosen lines out of the cached
// pool straight away (optimistic) and puts them back if the server refuses.
import { useMutation, useQuery, useQueryClient } from "react-query";
import { InquiryDetailType } from "types/InquiryTypes";
import {
  apiCreateEnquiryFromLines,
  apiGetAwaitingEnquiry,
  apiGetOrderPurchasing,
  apiReleaseEnquiryRequisitionLine,
  apiSetEnquiryRequisitionLines,
  apiSetEnquiryLinePurchase,
  AwaitingLineType,
  ComposeBodyType,
  LinePurchaseBodyType,
} from "util/network/RequisitionPool";
import { requisitionKeys } from "./useRequisitions";

export const poolKeys = {
  awaiting: () => [...requisitionKeys.all, "awaiting-enquiry"] as const,
  // Draft Inquiries a pool line can still be added to.
  drafts: () => ["inquiries", "drafts-for-pool"] as const,
  // The Purchasing panel on a Sales Order (P3).
  orderPurchasing: (orderId: number) =>
    ["orders", orderId, "purchasing"] as const,
};

export const useAwaitingEnquiry = (token: string) =>
  useQuery(poolKeys.awaiting(), () => apiGetAwaitingEnquiry(token), {
    enabled: !!token,
    staleTime: 10_000,
  });

type PoolSnapshot = { previous?: AwaitingLineType[] };

/** Drop these lines from the cached pool; returns the old rows for rollback. */
const useTakeFromPool = () => {
  const queryClient = useQueryClient();
  return async (lineIds: number[]): Promise<PoolSnapshot> => {
    await queryClient.cancelQueries(poolKeys.awaiting());
    const previous = queryClient.getQueryData<AwaitingLineType[]>(
      poolKeys.awaiting()
    );
    if (previous) {
      const taken = new Set(lineIds);
      queryClient.setQueryData(
        poolKeys.awaiting(),
        previous.filter((row) => !taken.has(row.requisition_item_id))
      );
    }
    return { previous };
  };
};

const useSettled = () => {
  const queryClient = useQueryClient();
  return {
    rollback: (context?: PoolSnapshot) => {
      if (context?.previous)
        queryClient.setQueryData(poolKeys.awaiting(), context.previous);
    },
    // The requisitions' line states and the Draft list both moved.
    refresh: () => {
      queryClient.invalidateQueries(requisitionKeys.all);
      queryClient.invalidateQueries(poolKeys.drafts());
    },
  };
};

export const useComposeEnquiry = (token: string) => {
  const take = useTakeFromPool();
  const { rollback, refresh } = useSettled();
  return useMutation<InquiryDetailType, any, ComposeBodyType, PoolSnapshot>(
    (body) => apiCreateEnquiryFromLines(token, body),
    {
      onMutate: (body) => take(body.lines),
      onError: (_err, _body, context) => rollback(context),
      onSettled: refresh,
    }
  );
};

export const useSetEnquiryLines = (token: string) => {
  const take = useTakeFromPool();
  const { rollback, refresh } = useSettled();
  return useMutation<
    InquiryDetailType,
    any,
    { inquiryId: number; lines: number[] },
    PoolSnapshot
  >(
    ({ inquiryId, lines }) =>
      apiSetEnquiryRequisitionLines(token, inquiryId, lines),
    {
      // Lines the Draft gives up come back with the refetch.
      onMutate: ({ lines }) => take(lines),
      onError: (_err, _vars, context) => rollback(context),
      onSettled: refresh,
    }
  );
};

export const useReleaseEnquiryLine = (token: string) => {
  const { refresh } = useSettled();
  return useMutation<
    InquiryDetailType,
    any,
    { inquiryId: number; requisitionItemId: number }
  >(
    ({ inquiryId, requisitionItemId }) =>
      apiReleaseEnquiryRequisitionLine(token, inquiryId, requisitionItemId),
    { onSettled: refresh }
  );
};

type PurchaseVars = { itemId: number; body: LinePurchaseBodyType };

/** What one Inquiry line buys (P3). The Inquiry dialog holds its own copy of
 * the inquiry rather than a cached query, so the optimistic patch goes
 * through `apply`: the guess first, the server's answer (with the new
 * suggestion and line text) on success, the old copy on failure. */
export const useSetLinePurchase = (
  token: string,
  inquiry: InquiryDetailType,
  apply: (next: InquiryDetailType) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    InquiryDetailType,
    any,
    PurchaseVars,
    { previous: InquiryDetailType }
  >(
    ({ itemId, body }) =>
      apiSetEnquiryLinePurchase(token, inquiry.id, itemId, body),
    {
      onMutate: ({ itemId, body }) => {
        const previous = inquiry;
        apply({
          ...inquiry,
          items: inquiry.items.map((item) => {
            if (item.id !== itemId) return item;
            const next = { ...item };
            if ("quantity" in body) {
              // A reset (null) is recounted by the server; its answer replaces this.
              next.quantity = body.quantity ?? item.quantity;
              next.quantityOverridden = body.quantity != null;
            }
            if (body.stockLengthMm !== undefined)
              next.stockLengthMm = body.stockLengthMm;
            if ("serviceQuantity" in body)
              next.serviceQuantity = body.serviceQuantity ?? null;
            if ("serviceUnit" in body)
              next.serviceUnit = (body.serviceUnit ??
                null) as typeof item.serviceUnit;
            return next;
          }),
        });
        return { previous };
      },
      onSuccess: (saved) => apply(saved),
      onError: (_err, _vars, context) => context && apply(context.previous),
      // The order panels' counts and shares follow the line.
      onSettled: () =>
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "orders" &&
            query.queryKey[2] === "purchasing",
        }),
    }
  );
};

export const useOrderPurchasing = (
  token: string,
  orderId: number | undefined
) =>
  useQuery(
    poolKeys.orderPurchasing(orderId ?? 0),
    () => apiGetOrderPurchasing(token, orderId as number),
    { enabled: !!token && !!orderId, staleTime: 10_000 }
  );
